import React, { useState, useEffect, useCallback } from "react";

import Section from "../section/Section";
import ProductGrid from "../product/ProductGrid";
import ProductFilters from "../productFilters/productFilters";
import ProductSearch from "../productFilters/productSearch/productSearch";
import { useDesktopMediaQuery } from "../../utils/helpers";
import { useShopContext } from "../../context/ShopContext";
import { useUser } from "../../context/UserContext";
import { ProductService } from "../../services/product.service";
import { Product } from "../../commonTypes";

import styles from "./collectionSection.module.css";
import { useRouter } from "next/router";

const groupedFilters = {
  "size-6-12m": [
    "size-6-12m",
    "size-6-9m",
    "size-9-12m",
    "size-9m",
    "size-12m",
    "size-medium",
    "size-1y",
    "size-68/74-(6-9m)",
    "size-74/80-(9-12m)",
  ],
  "size-1-2y": [
    "size-1-2y",
    "size-12-18m",
    "size-18m-24m",
    "size-18m",
    "size-24m",
    "size-18-24m",
    "size-xs",
    "size-1.5y",
    "size-2y",
    "size-large",
    "size-80/86-(12-18m)",
    "size-86/92-(18-24m)",
  ],
  "size-0-3m": [
    "size-0-3m",
    "size-1m",
    "size-3m",
    "size-56/62-(0-3m)",
    "size-2m",
    "size-small",
  ],
  "size-3-6m": [
    "size-3-6m",
    "size-6m",
    "size-4m",
    "size-62/68-(3-6m)",
    "size-small",
  ],
  "size-2-3y": [
    "size-9m-4y",
    "size-2-3y",
    "size-2-6y",
    "size-36m",
    "size-2",
    "size-s",
    "size-2-4y",
    "size-92/98-(2-3y)",
    "size-3y",
    "size-extra-large",
  ],
  "size-3-4y": [
    "size-9m-4y",
    "size-2-6y",
    "size-3-4y",
    "size-48m",
    "size-2-4y",
    "size-3",
    "size-m",
    "size-4",
    "size-4y",
    "size-98/104-(3-4y)",
    "size-4-5y",
  ],
  "size-5-6y": ["size-5-6y", "size-2-6y", "size-6", "size-6y", "size-4-5y"],
  "size-6-7y": ["size-6-7y", "size-6-10y"],
  "size-shoes": [
    "size-shoes",
    "size-19",
    "size-20",
    "size-21",
    "size-22",
    "size-23",
    "size-24",
    "size-25",
    "size-26",
    "size-27",
    "size-uk-6",
    "size-uk-7",
    "size-uk-8",
  ],
};

const PAGE_SIZE = 20;

const CollectionSection = () => {
  const [productList, setProductList] = useState<Product[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const { subscriptionOption } = useShopContext();
  const { userProfile } = useUser();
  const isDesktop = useDesktopMediaQuery();
  const router = useRouter();
  const [activeFilters, setActiveFilter] = useState([]);
  const [searchTxt, setSearchTxt] = useState<string | null>(null);
  const [isRouterReady, setIsRouterReady] = useState(false);
  const [indexOfPreviousProd, setIndexOfPreviousProd] = useState<number | null>(
    null
  );

  useEffect(() => {
    if (router.isReady) {
      if (router.query.q) {
        // @ts-ignore
        setActiveFilter(
          typeof router.query.q === "string" ? [router.query.q] : router.query.q
        );
      } else {
        setActiveFilter([]);
      }

      if (router.query.s) {
        setSearchTxt(router.query.s.toString());
      } else {
        setSearchTxt(null);
      }

      setIsRouterReady(true);
    }
  }, [router.isReady, router.query.q, router.query.s]);

  useEffect(() => {
    if (!router.query.select_plan) {
      if (window.sessionStorage.getItem("last-viewed-product")) {
        setIndexOfPreviousProd(
          Number(
            window.sessionStorage.getItem("last-viewed-product").split("+")[1]
          )
        );
        setTimeout(() => {
          // Make sure the products are rendered
          const productSlug = window.sessionStorage
            .getItem("last-viewed-product")
            ?.split("+")[0];

          if (productSlug) {
            const productElement = document.getElementById(`product-${productSlug}`);
            if (productElement) {
              productElement.scrollIntoView({ behavior: "smooth", block: "center" });
            }
          }
          window.sessionStorage.removeItem("last-viewed-product");
        }, 300);
      } else {
        setTimeout(() => {
          document
            ?.querySelector("#collection-shop")
            ?.scrollIntoView({ behavior: "smooth" });
        }, 300);
      }
    }
  }, [subscriptionOption, router?.query?.select_plan, indexOfPreviousProd]);

  // Expand grouped filters and fetch paginated products
  const fetchProducts = useCallback(
    async (pageNum: number, append: boolean = false) => {
      setIsLoading(true);

      try {
        // Check if we should only show available products (default behavior)
        const onlyShowActive =
          activeFilters.indexOf("product-not-available") < 0;

        // Build expanded filter list (excluding meta-filters)
        let allActiveFilters = [...activeFilters].filter(
          (itm) => itm !== "product-not-available" && itm !== "previously-rented"
        );

        // Add "product-available" filter when user wants to see only available products
        if (onlyShowActive) {
          allActiveFilters.push("product-available");
        }

        // Expand grouped filters (e.g., "size-6-12m" -> multiple size slugs)
        // and remove the grouped filter itself (it's not a real tag)
        activeFilters.forEach((filter) => {
          const groupedIndex = Object.keys(groupedFilters).indexOf(filter);
          if (groupedIndex > -1) {
            // Remove the grouped filter and add the expanded individual filters
            allActiveFilters = allActiveFilters.filter((f) => f !== filter);
            allActiveFilters = [...allActiveFilters, ...groupedFilters[filter]];
          }
        });

        // Check if we have size filters (for size-specific availability filtering)
        const sizeFilters = allActiveFilters.filter(
          (filter) => filter.indexOf("size") > -1
        );
        const hasSizeFilters = sizeFilters.length > 0;

        // Build filter object for API
        const filters = {
          tagSlugs: allActiveFilters.length > 0 ? allActiveFilters : undefined,
          searchText: searchTxt || undefined,
          onlyAvailable: onlyShowActive && hasSizeFilters,
          previousProductVariantIds:
            activeFilters.indexOf("previously-rented") > -1
              ? userProfile?.previousProductVariants
              : undefined,
        };

        const result = await ProductService.getPaginatedProducts(
          filters,
          pageNum,
          PAGE_SIZE
        );

        if (append) {
          setProductList((prev) => [...(prev || []), ...result.products]);
        } else {
          setProductList(result.products);
        }

        setHasMore(result.hasMore);
        setPage(pageNum);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setIsLoading(false);
      }
    },
    // Don't include cart - it causes re-fetching on every cart change
    [activeFilters, searchTxt, userProfile?.previousProductVariants]
  );

  // Fetch products when filters or search change (only after router is ready)
  useEffect(() => {
    if (isRouterReady) {
      fetchProducts(1, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRouterReady, activeFilters, searchTxt, userProfile?.previousProductVariants]);

  // Load more products (next page)
  const handleLoadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      fetchProducts(page + 1, true);
    }
  }, [fetchProducts, page, isLoading, hasMore]);

  return (
    <div id="collection-shop">
      <Section disableTabletPadding>
        <div className={styles.columnContainer}>
          {isDesktop && <ProductSearch activeFilters={activeFilters} />}
          <div className={styles.container}>
            <div className={styles.filters}>
              <ProductFilters activeFilters={activeFilters} />
            </div>
            <div className={styles.product}>
              <ProductGrid
                products={productList || []}
                indexOfPreviousProd={indexOfPreviousProd}
                onLoadMore={handleLoadMore}
                hasMore={hasMore}
                isLoading={isLoading}
              />
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default CollectionSection;
